import { ReactElement, SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import styles from './Button.module.scss';
import { Loader } from '../Loader/Loader';
import { getDataTest } from '../../utils/dataTests';

export interface ButtonProps {
    children: ReactElement | string;
    variant: 'primary' | 'secondary' | 'tertiary' | 'link' | 'white';
    submit?: boolean;
    small?: boolean;
    outlined?: boolean;
    href?: string;
    onClick?: (e?: SyntheticEvent) => void;
    className?: string;
    renderAsLabel?: boolean;
    htmlFor?: string;
    external?: boolean;
    disabled?: boolean;
    loading?: boolean;
    active?: boolean;
    ariaLabel?: string;
}

export const Button = ({
    children,
    variant,
    outlined,
    disabled,
    loading,
    small,
    href,
    submit,
    onClick,
    className,
    renderAsLabel,
    htmlFor,
    external,
    active,
    ariaLabel,
    ...props
}: ButtonProps): ReactElement => {
    const classnames = cn(
        styles.wrapper,
        className,
        disabled && styles.disabled,
        variant && styles[variant],
        outlined && styles.outlined,
        loading && styles.loading,
        small && styles.small,
        active && styles.active
    );

    const content = (
        <>
            <Loader
                className={styles.loader}
                size={small ? 'xsmall' : 'small'}
                color="unset"
                data-test={getDataTest(props, 'Loader')}
            />
            <span className={styles.content}>{children}</span>
        </>
    );

    if (!onClick && !href) {
        href = '#';
    }

    if (renderAsLabel && htmlFor) {
        return (
            <label htmlFor={htmlFor} className={classnames} {...props}>
                {content}
            </label>
        );
    }

    if (submit) {
        return (
            <button
                type="submit"
                aria-label={ariaLabel ? ariaLabel : undefined}
                className={classnames}
                disabled={disabled}
                {...props}
            >
                {content}
            </button>
        );
    }

    if (onClick) {
        return (
            <button
                type="button"
                aria-label={ariaLabel ? ariaLabel : undefined}
                onClick={onClick}
                className={classnames}
                disabled={disabled}
                {...props}
            >
                {content}
            </button>
        );
    }

    if (external && href) {
        return (
            <a href={href} className={classnames} aria-label={ariaLabel ? ariaLabel : undefined} {...props}>
                {content}
            </a>
        );
    }

    return (
        <Link to={href ?? '/'} className={classnames} aria-label={ariaLabel ? ariaLabel : undefined} {...props}>
            {content}
        </Link>
    );
};
